/* Theme custom file */
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/**
 * Homepage hero product rail: click tracking and live stock.
 *
 * Loaded by `hero-product-rail.liquid`, which only renders when the hero
 * previews products, so a page without the enriched hero never runs this.
 *
 * Tracking: one delegated listener on the hero card turns a click into a
 * document CustomEvent (`hero_product_click`, `hero_image_click`,
 * `hero_cta_click`). `batch-integration.js` consumes them behind the marketing
 * consent gate, so nothing here decides what is sent, or whether it is sent at
 * all. Handlers stay synchronous and never call preventDefault: the event has
 * to be dispatched before the browser leaves the page, and leaving the page is
 * the point of the click.
 *
 * Live stock: the hero is rendered with the page and then served from cache, so
 * a previewed product can sell out before a visit. On idle the module re-fetches
 * the same selection from the fetch-only `hero-products-refresh` section, swaps
 * the cards when the selection has moved on, and removes the rail when the sale
 * has nothing left in stock. Anything unexpected leaves the served rail alone --
 * it links to products that exist, which is worse than fresh but better than
 * wrong.
 */

const RAIL_SELECTOR = '.hero-product-rail';
const ITEM_SELECTOR = '.hero-product-rail__item';
const REFRESH_SECTION_ID = 'hero-products-refresh';

/**
 * The rail's product ids, in rail order.
 * @param {Element} rail
 * @returns {string[]}
 */
function productIds(rail) {
  return [...rail.querySelectorAll(ITEM_SELECTOR)].map((item) => item.dataset.productId ?? '');
}

/**
 * Dispatches a hero tracking event. The sale is read off the rail because it is
 * the one node both the server render and the refresh section emit.
 * @param {string} name
 * @param {Element} rail
 * @param {Record<string, string | number>} [detail]
 */
function dispatch(name, rail, detail = {}) {
  document.dispatchEvent(
    new CustomEvent(name, {
      detail: {
        operation_id: rail.dataset.operationId ?? '',
        operation_name: rail.dataset.operationName ?? '',
        ...detail,
      },
    })
  );
}

/**
 * One delegated listener for the whole hero: a card click identifies its
 * product, and the call to action identifies itself. The overlay link covers
 * everything else, the logo included, so any click that is neither a card nor
 * the CTA is a click on the hero image.
 * @param {Element} hero
 * @param {Element} rail
 */
function listenForClicks(hero, rail) {
  const onClick = (event) => {
    // auxclick covers middle-click open-in-new-tab; right-click is a context
    // menu, not a navigation.
    if (event.type === 'auxclick' && event.button !== 1) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // The quick add button opens a modal, not the product.
    if (target.closest('.product-card-alt__quick-add')) return;

    const item = target.closest(ITEM_SELECTOR);
    if (item) {
      dispatch('hero_product_click', rail, {
        item_id: item.dataset.productId ?? '',
        item_name: item.dataset.productName ?? '',
        position: Number(item.dataset.position) || 0,
      });
      return;
    }

    if (target.closest('.operation-card__cta')) {
      dispatch('hero_cta_click', rail);
      return;
    }

    if (target.closest('.operation-card__link')) {
      dispatch('hero_image_click', rail);
    }
  };
  hero.addEventListener('click', onClick);
  hero.addEventListener('auxclick', onClick);
}

/**
 * Replaces the rail's cards with the ones the section renders now, when the
 * in-stock selection has changed. Bails on anything that is not plainly the
 * same sale with a usable rail.
 * @param {Element} rail
 */
async function refreshRail(rail) {
  // The swapped-in cards would arrive without the ABradery price rewrite
  // (`price-test.js` processes `data-ab-b` at load), so while the price A/B
  // test is running the served cards stay: a stale card beats a wrong price.
  if (document.querySelector('[data-ab-b]')) return;

  let html;
  try {
    // Fetched from the bare root rather than `location.href`, so campaign
    // parameters (utm, gclid) do not fan the request out into one uncacheable
    // URL per ad click. `no-store` keeps the browser's HTTP cache out of a
    // request whose whole point is freshness; Shopify's edge cache still
    // applies and bounds how fresh the answer can be.
    const url = `${window.Shopify?.routes?.root ?? '/'}?section_id=${REFRESH_SECTION_ID}`;
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) return;
    html = await response.text();
  } catch {
    return; // network or section error: the served cards stay
  }

  const source = new DOMParser().parseFromString(html, 'text/html').querySelector(RAIL_SELECTOR);
  // No rail at all in the response: the sale ended or lost its flag, so there is
  // nothing to say about the hero the visitor is looking at.
  if (!source) return;
  // The homepage leads with another sale now, so these cards belong to nothing
  // the visitor is looking at. A reload will show the new hero.
  if (source.dataset.operationId !== rail.dataset.operationId) return;

  const items = [...source.querySelectorAll(ITEM_SELECTOR)];
  // Same sale, nothing in stock. The section renders the rail root empty for
  // exactly this case, so an empty rail is an answer, not a failure: drop it and
  // the hero falls back to the logo and CTA the server renders for a sold-out
  // sale, instead of keeping four cards that all lead to a sold-out product.
  if (items.length === 0) {
    rail.remove();
    return;
  }
  if (productIds(source).join(',') === productIds(rail).join(',')) return;

  rail.replaceChildren(...items.map((item) => document.importNode(item, true)));
}

onDocumentReady(() => {
  const rail = document.querySelector(RAIL_SELECTOR);
  if (!rail) return;

  const hero = rail.closest('.operation-card');
  if (hero) listenForClicks(hero, rail);

  requestIdleCallback(() => refreshRail(rail));
});
